import { COLLAPSIBLE_TOOLS, HOISTED_SUBAGENT_RESULT_TOOLS } from "../../constants/tools"
import type { InnerMessage, ToolStep, ToolStepStatus } from "./ToolCallGroup"

/** Every event type the BE emits on the stream (see `simple_agent.py`). */
export type WireEventType =
  | "text"
  | "thinking"
  | "tool_call"
  | "tool_result"
  | "tool_status"
  | "status"
  | "keepalive"
  | "addin_content_request"
  | "artifact_updated"
  | "interrupted"
  | "error"
  | "extraction_progress"
  | "file"

/** Discriminator for `turnArtifacts.type`. Adds the synthetic `tool_group`
 * value emitted FE-side by `flushGroup` for tool-group cards. */
export type TurnArtifactType = WireEventType | typeof TOOL_GROUP_MESSAGE_TYPE

/**
 * Subset of `turnArtifacts` fields the grouper actually reads or writes.
 * All optional because we accept history rows from older shapes that may
 * lack any of these. The open index signature keeps the type compatible
 * with the upstream `Record<string, any>`-typed artifacts (callers pass
 * `ConversationMessage[]` through unchanged) and with `ToolGroupArtifacts`,
 * which adds `steps` / `isStreaming` not present here.
 */
export interface GroupableTurnArtifacts {
  type?: TurnArtifactType
  tool_call_id?: string
  tool_name?: string
  source_tool_call_id?: string
  /** Set by `agent/tools/sub_agent.py` on every event it re-wraps. The
   * grouper uses this to pull child events out of the top-level stream
   * and attach them as `innerMessages` on the parent sub_agent step. */
  parent_tool_call_id?: string
  parent_tool_name?: string
  /** Original event type before sub_agent.py re-wrapped the event as
   * `type: "tool_result"`. Sub_agent only re-wraps wire events, so the
   * synthetic `tool_group` value can never appear here. */
  wrapped_type?: WireEventType
  /** Terminal marker emitted by sub_agent.py's finally block for the
   * outer sub_agent step (legacy completion signal). */
  done?: boolean
  streaming?: boolean
  /** Per-tool-call interrupt scoping. Set by interruption events. */
  interrupted_tool_call_ids?: string[]
  /** Tool-status terminal discriminator ("done" / "error"); set on
   * `tool_status` events. Wire field; grouper itself doesn't read it,
   * but streaming consumers preserve it. */
  status?: string
  /** Free-form tool-status message (e.g. error detail). Wire field. */
  message?: string
  subject?: string
  /** Wire field on tool_result events: Word document anchor names for
   * redline/comment apply (addin-only consumer). BE serializer at
   * `simple_agent.py` promotes `content.bookmarks` to the event's top
   * level and `turn_persistence.py` spreads it into the persisted row. */
  bookmarks?: string[]
  /** Wire field on tool_result events: citations array. Shape is the
   * addin's `Citation` (in `addin/src/types/chat.ts`) — kept loose here
   * because common/ shouldn't depend on src/. Consumers narrow at use. */
  citations?: unknown[]
  /** Wire field: BE-emitted ISO timestamp on the originating event. The
   * grouper relies on the top-level `timestamp` on the message row, but
   * persistence spreads the whole event into `turn_artifacts`, so this
   * field ends up here too on history rows. */
  timestamp?: string
  /** UTC tz-aware ISO timestamps stamped BE-side per inner tcid (first-emit
   * wins). The @tool decorator and `sub_agent.py` set these on the events
   * they emit, and the persistence layer spreads the whole event into
   * `turn_artifacts` — so both live and rehydrated events carry them.
   * `buildStep` prefers these over the row's `created_at` / `completed_at`
   * column for elapsed math, because the row timestamps reflect persistence
   * time (which lags chunked tool_call accumulators) rather than the real
   * tool start/end. */
  started_at?: string
  completed_at?: string
  answer?: unknown
  user_feedback?: 0 | 1
}

/**
 * A minimal subset of the addin's `ChatMessage` we need to walk. Kept narrow
 * so this module stays usable from the frontend later without dragging
 * addin-specific types in.
 *
 * Discriminated on `messageType`: the synthetic `tool_group` row carries
 * `ToolGroupArtifacts` (with `steps`/`isStreaming`); every other row is a
 * pass-through wire event with `GroupableTurnArtifacts`.
 */
interface GroupableMessageBase {
  id?: string
  turnType: "user" | "assistant"
  content: string
  toolName?: string
  timestamp?: string
  // Terminal-state timestamp on the persisted row. BE writes this on terminal
  // events (matching tool_result arrival, tool_status: done/error/interrupted,
  // end_turn, interrupt). NULL while the row is still streaming. Authoritative
  // signal for "when did this step actually finish".
  completedAt?: string
  turnCompletedAt?: string
  isTransient?: boolean
}

export interface GroupableToolGroupMessage extends GroupableMessageBase {
  messageType: typeof TOOL_GROUP_MESSAGE_TYPE
  turnArtifacts: ToolGroupArtifacts
}

export interface GroupablePassthroughMessage extends GroupableMessageBase {
  messageType?: WireEventType
  turnArtifacts?: GroupableTurnArtifacts
}

export type GroupableMessage = GroupableToolGroupMessage | GroupablePassthroughMessage

/** Status snapshot tracked by the streaming consumer for one tool call. */
const NON_TERMINAL_TOOL_STATUSES: ReadonlySet<string> = new Set(["running", "processing", "pending"])

function isNonTerminalToolStatusRow(m: GroupableMessage): boolean {
  const status = m.turnArtifacts?.status
  return getMessageTypeOf(m) === "tool_status" && status !== undefined && NON_TERMINAL_TOOL_STATUSES.has(status)
}

export interface ToolStatusSnapshot {
  status: ToolStepStatus
  startedAt: number
  completedAt?: number
  errorMessage?: string
  subject?: string
  // Stored alongside startedAt so the snapshot hook's transition log can
  // print a human-readable tool name (the buildStep consumer already has
  // `m.toolName`, but the hook fires on raw stream events).
  toolName?: string
}

/**
 * Marker messageType emitted by the grouper for a synthetic "card" message.
 * The Virtuoso renderer keys off this to swap in a <ToolCallGroup />.
 */
export const TOOL_GROUP_MESSAGE_TYPE = "tool_group" as const

export interface ToolGroupArtifacts extends GroupableTurnArtifacts {
  type: typeof TOOL_GROUP_MESSAGE_TYPE
  steps: ToolStep[]
  isStreaming: boolean
  turnOpen: boolean
}

const ASSISTANT_GROUPABLE_TYPES = new Set([
  "tool_call",
  "tool_result",
  "tool_status",
  "status",
])

/** Tools rendered by bespoke widgets (TodoListWidget, RedlineSection, etc.).
 * At the top level their `tool_result` messages must remain standalone rows
 * — they have their own apply/dismiss UI rendered by `MessageItem.tsx`. But
 * inside a sub-agent's mini-chat we don't render those widgets (the actions
 * panel above the chat already aggregates them globally), so they fold into
 * the inner tool group like every other call. The `nested` flag passed
 * through `groupImpl` toggles this. */
const BESPOKE_RESULT_TOOLS = new Set([
  "redline_selection",
  "redline_section",
  "edit_table",
  "change_throughout",
  "add_paragraph",
  "add_comment",
  "reply_to_comment",
  "initialize_new_connection",
  "send_guideline_suggestion",
  "suggest_skill",
  "suggest_playbook_selection",
  "create_playbook_workspace",
  "create_workspace_from_blueprint",
  "todo_list",
  "live_review",
  "initialize_review",
  "check_icertis_auth",
  "disconnect_icertis",
  "suggest_template_selection",
  "collect_input",
  "draft_email",
  "render_agreement_review_form",
  "present_agreement",
  "agentic_workflow_run",
])

function getMessageTypeOf(m: GroupableMessage): TurnArtifactType {
  return m.messageType || m.turnArtifacts?.type || "text"
}

/** True for a sub-agent-wrapped tool_result we want to surface at the top level
 * instead of inside the mini-chat (redline-family OOXML cards and suggest_*
 * JSON suggestion cards alike — see HOISTED_SUBAGENT_RESULT_TOOLS).
 *
 * We deliberately do NOT key on `wrapped_type`. Every sub-agent inner event
 * (tool_call, tool_result, tool_status) is re-wrapped with the SAME outer
 * `type: "tool_result"` and the SAME inner `tool_call_id`, so on the FE
 * `updateLastToolResultMessage` merges all three into ONE message keyed by
 * tcid (ChatContext.tsx). That merge spreads `...existing, ...new`, so
 * `wrapped_type` ends up as whatever arrived LAST — the `tool_status` event,
 * not `tool_result`. Requiring `wrapped_type === "tool_result"` therefore
 * missed every completed result (it had already been overwritten to
 * "tool_status"), leaving it buried in the mini-chat with no card. The merged
 * row is reliably a hoist target by: parent is sub_agent + outer type is
 * tool_result + the tool is in the allowlist. The payload is carried in
 * `content` regardless of `wrapped_type`. */
function isHoistedSubAgentResult(m: GroupableMessage): boolean {
  const a = m.turnArtifacts
  if (a?.parent_tool_name !== "sub_agent") return false
  if (getMessageTypeOf(m) !== "tool_result") return false
  const toolName = m.toolName ?? a?.tool_name
  return !!toolName && HOISTED_SUBAGENT_RESULT_TOOLS.has(toolName)
}

function isAssistantGroupable(m: GroupableMessage, nested: boolean): boolean {
  if (m.turnType !== "assistant") return false
  const type = getMessageTypeOf(m)
  if (!ASSISTANT_GROUPABLE_TYPES.has(type)) return false
  if (type === "status" && !(m.toolName ?? m.turnArtifacts?.tool_name)) return false
  if (
    !nested &&
    type === "tool_result" &&
    m.toolName &&
    BESPOKE_RESULT_TOOLS.has(m.toolName)
  ) {
    return false
  }
  return true
}

/**
 * Walk a flat assistant/user message list and roll up consecutive
 * tool-call / tool-result / status messages into synthetic `tool_group`
 * messages whose `turnArtifacts.steps` powers a <ToolCallGroup /> card.
 *
 * Sub-agent children — every event tagged with
 * `parent_tool_name === "sub_agent"` by `agent/tools/sub_agent.py` — are
 * pulled out of the top-level stream and attached as `innerMessages` on the
 * parent sub_agent step. `innerMessages` is itself the output of a recursive
 * `groupAssistantSteps` call, so a sub-agent's chat renders as a mini chat
 * (text bubbles + nested <ToolCallGroup /> cards) and nested sub-agents
 * compose to arbitrary depth ("infinite mirror").
 *
 * The status of each step is sourced from `toolStatusByCallId`, which the
 * streaming consumer maintains from `tool_call` (start) and `tool_status`
 * (done/error) events. If no entry exists we fall back to inferring `done`
 * from the presence of a `tool_result` or terminal marker.
 */
export function groupAssistantSteps<T extends GroupableMessage>(
  messages: T[],
  toolStatusByCallId: Map<string, ToolStatusSnapshot>,
): (T | GroupableToolGroupMessage)[] {
  // Collected once at the outermost call and threaded through every nested
  // `groupImpl` so inner mini-chats can attach the events of their grand-
  // children without each level having to re-walk the full message list.
  const innerByParent = collectInnerMessages(messages)
  return groupImpl(messages, toolStatusByCallId, innerByParent, false, new Set())
}

export function toolGroupSteps(message: { turnArtifacts?: unknown }): ToolStep[] {
  return (message.turnArtifacts as ToolGroupArtifacts | undefined)?.steps ?? []
}

export function isToolGroupStreaming(message: { turnArtifacts?: unknown }): boolean {
  return !!(message.turnArtifacts as ToolGroupArtifacts | undefined)?.isStreaming
}

function collectInnerMessages(
  messages: GroupableMessage[],
): Map<string, GroupableMessage[]> {
  const innerByParent = new Map<string, GroupableMessage[]>()
  for (const m of messages) {
    const parentToolCallId = m.turnArtifacts?.parent_tool_call_id
    const parentToolName = m.turnArtifacts?.parent_tool_name
    if (!parentToolCallId || parentToolName !== "sub_agent") continue
    // Hoisted tool_results are lifted to top-level standalone cards (see
    // isHoistedSubAgentResult); keep them OUT of the mini-chat bucket so they
    // render once, at the top level, instead of as a bare nested step.
    if (isHoistedSubAgentResult(m)) continue
    // The synthetic `done: true` marker emitted by sub_agent.py's finally
    // block is a parent-status signal, not visible inner content.
    const wrappedType = m.turnArtifacts?.wrapped_type
    if (m.turnArtifacts?.done === true && !wrappedType) continue
    // Pre-`wrapped_type` history rows only carry `type === "tool_result"` —
    // surface them as text bubbles so old conversations still render the
    // sub-agent's monologue rather than a stack of opaque tool results.
    let innerType: WireEventType = wrappedType ?? "text"
    // Self-reference guard: a wrapped event whose `tool_call_id` equals its
    // `parent_tool_call_id` AND whose `tool_name` is "sub_agent" cannot
    // legitimately be a *child* sub_agent step inside its own parent — it's
    // sub_agent.py's own wrapper stamping both fields with the parent's tcid
    // (which it does for any inner event missing both). Treating such a row
    // as a groupable child would make `innerByParent[X]` contain a sub_agent
    // step with toolCallId === X, and the recursive grouper would loop on
    // itself ("Maximum call stack size exceeded"). Force these to render as
    // text bubbles — that's the only legitimate shape they can have, and it
    // also matches the rendering for older history rows without
    // `wrapped_type` at all.
    const tcid = m.turnArtifacts?.tool_call_id
    const toolName = m.toolName ?? m.turnArtifacts?.tool_name
    if (
      tcid === parentToolCallId &&
      toolName === "sub_agent" &&
      innerType !== "text"
    ) {
      innerType = "text"
    }
    const stripped: GroupableMessage = {
      id: m.id,
      turnType: "assistant",
      content: m.content,
      messageType: innerType,
      timestamp: m.timestamp,
      toolName: m.toolName,
      // Reify the original type and strip parent_* so the recursive grouper
      // treats this list as a flat conversation. The original parent linkage
      // is already captured in `innerByParent`'s key.
      turnArtifacts: {
        ...m.turnArtifacts,
        type: innerType,
        parent_tool_call_id: undefined,
        parent_tool_name: undefined,
      },
    }
    const arr = innerByParent.get(parentToolCallId) ?? []
    arr.push(stripped)
    innerByParent.set(parentToolCallId, arr)
  }
  return innerByParent
}

function groupImpl<T extends GroupableMessage>(
  messages: T[],
  toolStatusByCallId: Map<string, ToolStatusSnapshot>,
  innerByParent: Map<string, GroupableMessage[]>,
  nested: boolean,
  // Ancestor chain of sub_agent toolCallIds we're currently rendering inside
  // of. Used to break cycles: if we'd recurse with a tcid already in this
  // set, we'd loop on the same `innerByParent` bucket forever (see the
  // self-reference guard in `collectInnerMessages` for the most common
  // shape that triggers it). Bail out and leave `innerMessages` unset so
  // the row renders without a mini-chat instead of crashing the whole list.
  ancestorToolCallIds: Set<string>,
): (T | GroupableToolGroupMessage)[] {
  // Pre-scan: derive each tool_call's terminal state from the persisted rows.
  //
  // Primary signal (post-migration 0158): `m.completedAt` on the tool_call row
  // itself. BE stamps it on matching tool_result arrival, terminal tool_status,
  // end_turn, or interrupt (see agent/turn_persistence.py:close_open_streams).
  // NULL means the row is still streaming.
  //
  // Secondary signal: a sibling `tool_status` row keyed to the same tcid.
  // The `status` field discriminates terminal kind (done / error / interrupted)
  // — this is what lets a history-loaded row paint red/yellow without a live
  // snapshot. The row's own `completedAt` (or its `timestamp` for legacy rows)
  // is also a fallback completion time when the tool_call row itself lacks one.
  //
  // Legacy fallback: for history rows persisted before this migration,
  // `completedAt` is NULL and we infer completion from the existing heuristics
  // (later assistant text, later user turn, sibling tool_result, presence-only
  // tool_status). Kept so old conversations don't all read as in_progress.
  //
  // Sub-agent wrapping caveat: events emitted by a sub-agent are re-wrapped by
  // `agent/tools/sub_agent.py` as `type: "tool_result"` tagged with the
  // parent's tool_call_id. We look at `wrapped_type` for the original
  // semantics, and we must NOT treat a wrapped inner event as completion proof
  // for the parent sub_agent step — only the explicit `done: true` marker
  // (legacy) or the dioptra `tool_status` event count.
  const completedToolCallIds = new Map<string, number>()
  // Map tool_call_id → marker timestamp (ms) for steps cut short by interrupt.
  // Populated from per-tcid `tool_status: interrupted` rows (new BE) or the
  // legacy parent marker `turn_artifacts={"type":"interrupted"}` row.
  const interruptedToolCallIds = new Map<string, number>()
  // Map tool_call_id → terminal status discriminator from its tool_status row,
  // used by `buildStep` to paint history-loaded rows correctly (without a live
  // snapshot). Only "done" / "error" — interrupted is tracked via
  // `interruptedToolCallIds` for its priority-over-snapshot semantics.
  const terminalStatusByTcid = new Map<string, "done" | "error">()
  const terminalErrorMessageByTcid = new Map<string, string>()
  const subjectByTcid = new Map<string, string>()
  const pendingToolCallIds: string[] = []
  // Only record completion when we have a real timestamp from the message.
  // Falling back to Date.now() makes a reloaded conversation's elapsed time
  // grow from the historical start to "right now" — the timer ticks even
  // though every event happened in the past.
  const recordCompletion = (tcid: string, tsMs: number | undefined, _reason: string) => {
    if (tsMs == null) return
    const prev = completedToolCallIds.get(tcid)
    if (prev === undefined || tsMs > prev) completedToolCallIds.set(tcid, tsMs)
  }
  const removePending = (tcid: string) => {
    const idx = pendingToolCallIds.indexOf(tcid)
    if (idx >= 0) pendingToolCallIds.splice(idx, 1)
  }
  for (const m of messages) {
    const tsMs = parseTimestampMs(m.timestamp)
    const completedAtMs = parseTimestampMs(m.completedAt)
    if (m.turnType === "user") {
      for (const tcid of pendingToolCallIds) recordCompletion(tcid, tsMs, "user-flush")
      pendingToolCallIds.length = 0
      continue
    }
    if (m.turnType !== "assistant") continue
    const t = getMessageTypeOf(m)
    const wrappedType = m.turnArtifacts?.wrapped_type
    const semanticType = wrappedType || t
    const parentToolCallId = m.turnArtifacts?.parent_tool_call_id

    if (semanticType === "tool_call") {
      if (parentToolCallId) continue
      const tcid = m.turnArtifacts?.tool_call_id
      if (!tcid) continue
      // BE-authoritative: a non-NULL completedAt on the tool_call row means
      // the tool has reached terminal state. Skip the pending list entirely —
      // we already know its end time.
      if (completedAtMs != null) {
        recordCompletion(tcid, completedAtMs, "tool_call.completedAt")
      } else {
        pendingToolCallIds.push(tcid)
      }
      continue
    }
    if (semanticType === "tool_status") {
      const tcid = m.turnArtifacts?.tool_call_id
      if (!tcid) continue
      const status = m.turnArtifacts?.status
      // tool_status rows are one-shot terminal — completedAt is stamped at
      // insert. Fall back to wire timestamp for legacy rows.
      const endMs = completedAtMs ?? tsMs
      const subject = m.turnArtifacts?.subject
      if (typeof subject === "string" && subject) subjectByTcid.set(tcid, subject)
      if (status !== undefined && NON_TERMINAL_TOOL_STATUSES.has(status)) {
        continue
      }
      if (status === "interrupted") {
        if (endMs != null) interruptedToolCallIds.set(tcid, endMs)
        removePending(tcid)
      } else if (status === "done" || status === "error") {
        recordCompletion(tcid, endMs, `tool_status:${status}`)
        terminalStatusByTcid.set(tcid, status)
        if (status === "error") {
          const msg = m.turnArtifacts?.message
          if (typeof msg === "string") terminalErrorMessageByTcid.set(tcid, msg)
        }
        removePending(tcid)
      } else {
        // Pre-status-discriminator legacy: bare presence proves completion.
        recordCompletion(tcid, endMs, "tool_status(legacy)")
        removePending(tcid)
      }
      continue
    }
    if (semanticType === "tool_result") {
      const tcid = m.turnArtifacts?.tool_call_id
      if (!tcid) continue
      if (m.turnArtifacts?.streaming === true) continue
      const isWrapForParent =
        parentToolCallId !== undefined && parentToolCallId === tcid
      if (isWrapForParent && m.turnArtifacts?.done !== true) continue
      recordCompletion(tcid, tsMs, `tool_result${isWrapForParent ? "(done:true)" : ""}`)
      continue
    }
    if (semanticType === "text") {
      if (parentToolCallId) continue
      for (const tcid of pendingToolCallIds) recordCompletion(tcid, tsMs, "text-flush")
      pendingToolCallIds.length = 0
    }
    if (semanticType === "interrupted") {
      // Marker can arrive three ways:
      //   1. Live wrapped event (sub-agent path) — `tool_result` envelope
      //      wrapping `{type:"interrupted"}`, carries the parent's tool_call_id
      //      for the sub_agent invocation that was cut. Only seen during the
      //      LIVE stream; bypasses persistence (parent_message_queue → HTTP).
      //   2. Persisted parent marker turn — empty-content assistant row with
      //      `turn_artifacts={"type":"interrupted", "interrupted_tool_call_ids":[…]}`,
      //      no own `tool_call_id`. The list is the authoritative reload signal
      //      because (1) is not in the DB.
      //   3. Legacy parent marker (pre-list) — same as (2) but no list field.
      //      Fall back to flushing pendingToolCallIds.
      const markerTs = tsMs ?? Date.now()
      const markerTcid = m.turnArtifacts?.tool_call_id
      const interruptedList = m.turnArtifacts?.interrupted_tool_call_ids
      let attributed = false
      if (markerTcid) {
        interruptedToolCallIds.set(markerTcid, markerTs)
        const idx = pendingToolCallIds.indexOf(markerTcid)
        if (idx >= 0) pendingToolCallIds.splice(idx, 1)
        attributed = true
      }
      if (Array.isArray(interruptedList)) {
        for (const tcid of interruptedList) {
          if (typeof tcid === "string" && tcid) {
            interruptedToolCallIds.set(tcid, markerTs)
            const idx = pendingToolCallIds.indexOf(tcid)
            if (idx >= 0) pendingToolCallIds.splice(idx, 1)
            attributed = true
          }
        }
      }
      if (!attributed) {
        for (const tcid of pendingToolCallIds) interruptedToolCallIds.set(tcid, markerTs)
        pendingToolCallIds.length = 0
      }
    }
  }

  const out: (T | GroupableToolGroupMessage)[] = []
  let currentSteps: ToolStep[] = []
  let currentMessageIds: string[] = []
  let currentAnchorTimestamp: string | undefined
  let currentTurnOpen = false
  // Tcids we've already produced a step for, across every group. When an
  // `interrupted` marker (or any non-groupable row) lands between a tool_call
  // and its matching tool_status, it calls flushGroup() and clears
  // currentSteps. A naive second buildStep on the tool_status row then
  // creates a fresh step in a new group with startedAt == completedAt ==
  // the tool_status timestamp — a zero-duration ghost. This set lets us
  // skip those duplicates while still merging when the original step is
  // still in currentSteps.
  const builtStepTcids = new Set<string>()

  const flushGroup = ({ trailing }: { trailing: boolean }) => {
    if (currentSteps.length === 0) return
    const isStreaming = currentSteps.some((s) => s.status === "in_progress")
    const artifacts: ToolGroupArtifacts = {
      type: TOOL_GROUP_MESSAGE_TYPE,
      steps: currentSteps,
      isStreaming,
      turnOpen: trailing && currentTurnOpen,
    }
    out.push({
      id: `tool-group-${currentMessageIds[0] ?? currentSteps[0]?.toolCallId ?? out.length}`,
      turnType: "assistant",
      content: "",
      messageType: TOOL_GROUP_MESSAGE_TYPE,
      timestamp: currentAnchorTimestamp,
      turnArtifacts: artifacts,
    })
    currentSteps = []
    currentMessageIds = []
    currentAnchorTimestamp = undefined
    currentTurnOpen = false
  }

  for (let i = 0; i < messages.length; i++) {
    const m = messages[i]

    // Children are folded under their parent; skip top-level emission — except
    // hoisted tool_results, which we surface as standalone top-level cards.
    const parentToolName =
      m.turnArtifacts?.parent_tool_name || (m as any).parent_tool_name
    if (parentToolName === "sub_agent" && !isHoistedSubAgentResult(m)) continue

    if (isAssistantGroupable(m, nested)) {
      const toolCallId = m.turnArtifacts?.tool_call_id || m.id || ""
      const stepKey = toolCallId || `${m.toolName ?? "tool"}-${i}`
      const existing = currentSteps.find((s) => s.toolCallId === stepKey)
      // Tool_status rows are pure metadata signals — the pre-scan above
      // already folded their terminal/interrupted state into the per-tcid
      // maps that buildStep consults. If we've already produced a step for
      // this tcid in a previous group (flushed earlier by a non-groupable
      // marker), skip the tool_status row entirely. Without this guard,
      // a refresh after a cancel renders both the correct interrupted step
      // and a duplicate 0s ghost. tool_call / tool_result rows still create
      // steps when needed (legacy data may lack a paired tool_call row).
      if (
        !existing &&
        getMessageTypeOf(m) === "tool_status" &&
        toolCallId &&
        builtStepTcids.has(toolCallId)
      ) {
        continue
      }
      if (isNonTerminalToolStatusRow(m)) {
        continue
      }
      const built = buildStep(m, toolStatusByCallId, stepKey, completedToolCallIds, interruptedToolCallIds, terminalStatusByTcid, terminalErrorMessageByTcid)
      if (!built) continue
      built.subject = toolStatusByCallId.get(built.toolCallId)?.subject ?? subjectByTcid.get(built.toolCallId)
      if (existing) {
        existing.resultContent = mergeContent(existing.resultContent, built.resultContent)
        if (built.status === "error") {
          existing.status = "error"
          existing.errorMessage = built.errorMessage ?? existing.errorMessage
        } else if (existing.status !== "error" && built.status === "done") {
          existing.status = "done"
        } else if (
          existing.status !== "error" &&
          existing.status !== "done" &&
          built.status === "interrupted"
        ) {
          existing.status = "interrupted"
        }
        existing.completedAt = built.completedAt ?? existing.completedAt
      } else {
        currentSteps.push(built)
        if (toolCallId) builtStepTcids.add(toolCallId)
      }
      currentMessageIds.push(m.id ?? `idx-${i}`)
      currentAnchorTimestamp = currentAnchorTimestamp ?? m.timestamp
      if (m.turnCompletedAt == null) currentTurnOpen = true
      // For sub_agent steps, recursively group the sub-agent's inner stream
      // and attach it as a mini-chat. Reusing the top-level `innerByParent`
      // map lets nested sub-agents pick up THEIR own children without a
      // second walk.
      if (built.toolName === "sub_agent") {
        const target = currentSteps.find((s) => s.toolCallId === stepKey)
        if (target && !target.innerMessages) {
          const inner = innerByParent.get(toolCallId)
          if (
            inner &&
            inner.length > 0 &&
            toolCallId &&
            !ancestorToolCallIds.has(toolCallId)
          ) {
            const nextAncestors = new Set(ancestorToolCallIds)
            nextAncestors.add(toolCallId)
            const innerGrouped = groupImpl(
              inner,
              toolStatusByCallId,
              innerByParent,
              true,
              nextAncestors,
            )
            // BE only emits per-tcid `tool_status: interrupted` for the
            // top-level pending tcids (simple_agent.py iterates only the
            // outermost persist_state.pending_tool_call_ids on stop).
            // Inner tools that were mid-flight when the user cancelled get
            // no terminal signal of their own — their `tool_call` row stays
            // unpaired, so buildStep falls through to "in_progress" and
            // their label renders present-tense ("Creating document") even
            // though the parent sub_agent is shown as cancelled. Propagate
            // the parent's interrupted state down so any in-flight inner
            // step adopts it.
            if (target.status === "interrupted" && target.completedAt != null) {
              propagateInterruptedToInnerSteps(innerGrouped, target.completedAt)
            }
            target.innerMessages = toInnerMessages(innerGrouped)
          }
        }
      }
      continue
    }

    flushGroup({ trailing: false })
    out.push(m)
  }

  flushGroup({ trailing: true })

  return out
}

function buildStep(
  m: GroupableMessage,
  toolStatusByCallId: Map<string, ToolStatusSnapshot>,
  fallbackKey: string,
  completedToolCallIds: Map<string, number>,
  interruptedToolCallIds: Map<string, number>,
  terminalStatusByTcid: Map<string, "done" | "error">,
  terminalErrorMessageByTcid: Map<string, string>,
): ToolStep | null {
  const type = getMessageTypeOf(m)
  const toolName = m.toolName ?? m.turnArtifacts?.tool_name
  if (!toolName) return null
  const toolCallId = m.turnArtifacts?.tool_call_id || fallbackKey

  const snapshot = toolStatusByCallId.get(toolCallId)
  const messageTimestamp = parseTimestampMs(m.timestamp)
  const ownCompletedAt = parseTimestampMs(m.completedAt)
  // BE-stamped per-tcid wire timestamps. Memoized first-emit-wins on the
  // emitter side, so they're a stable anchor across multi-chunk tool_call
  // accumulators and across the live→rehydrate boundary. Prefer these over
  // the row's `created_at` (which is persistence-time, not call-start-time)
  // and over the synthesized `Date.now()` fallbacks below.
  const wireStartedAt = parseTimestampMs(m.turnArtifacts?.started_at)
  const wireCompletedAt = parseTimestampMs(m.turnArtifacts?.completed_at)
  const derivedCompletedAt = completedToolCallIds.get(toolCallId) ?? wireCompletedAt ?? ownCompletedAt
  const interruptedAt = interruptedToolCallIds.get(toolCallId)
  const terminalKind = terminalStatusByTcid.get(toolCallId)
  let status: ToolStepStatus
  let startedAt: number
  let completedAt: number | undefined
  let errorMessage: string | undefined

  // Anchor used in every no-snapshot branch below. BE-stamped wire
  // `started_at` wins (per-tcid first-emit-wins ISO), with the row's
  // `created_at` next, and `Date.now()` only when neither is available.
  const fallbackStartedAt = wireStartedAt ?? messageTimestamp ?? Date.now()

  if (snapshot) {
    if (interruptedAt !== undefined) {
      // Interrupted marker always wins over a live snapshot — the snapshot
      // may still read in_progress because the flip hadn't landed in React
      // state, or because nothing flipped it at all (partial reload path).
      // For startedAt we prefer the wire anchor (same frame as interruptedAt
      // when both are BE-stamped) over snapshot.startedAt (real-UTC Date.now()).
      status = "interrupted"
      startedAt = wireStartedAt ?? messageTimestamp ?? snapshot.startedAt
      completedAt = interruptedAt
    } else {
      status = snapshot.status
      startedAt = snapshot.startedAt
      completedAt = snapshot.completedAt ?? derivedCompletedAt
      errorMessage = snapshot.errorMessage
    }
  } else if (interruptedAt !== undefined) {
    // History-loaded row cut short by the user pressing stop. Either a
    // per-tcid `tool_status: interrupted` row, or the legacy parent marker.
    startedAt = fallbackStartedAt
    status = "interrupted"
    completedAt = interruptedAt
  } else if (terminalKind !== undefined) {
    // BE-stamped terminal state from a sibling tool_status row.
    startedAt = fallbackStartedAt
    status = terminalKind
    completedAt = derivedCompletedAt ?? messageTimestamp
    errorMessage = terminalErrorMessageByTcid.get(toolCallId)
  } else if (derivedCompletedAt !== undefined) {
    // BE stamped completion (matching tool_result arrived, end_turn closed
    // open streams, etc.) but no explicit terminal-kind row exists — treat
    // as done.
    startedAt = fallbackStartedAt
    status = "done"
    completedAt = derivedCompletedAt
  } else {
    // No completion signal of any kind: row is genuinely mid-stream (live
    // session before stream reconnect), or pre-migration history row that
    // BE never stamped. Surface as in_progress and let any incoming live
    // snapshot resolve it.
    startedAt = fallbackStartedAt
    status = "in_progress"
  }

  // A streamed tool_result with the terminal sub_agent `done` marker confirms
  // completion even if we missed the tool_status event.
  if (type === "tool_result" && m.turnArtifacts?.done === true && status === "in_progress") {
    status = "done"
    completedAt = completedAt ?? messageTimestamp ?? Date.now()
  }


  const resultContent =
    type === "tool_result" && COLLAPSIBLE_TOOLS.has(toolName) && m.content
      ? m.content
      : undefined

  return {
    toolCallId,
    toolName,
    status,
    startedAt,
    completedAt,
    errorMessage,
    resultContent,
  }
}

/** Flip any still-in_progress step inside a sub_agent's grouped inner messages
 * to "interrupted" with the parent's completedAt. Used when the parent
 * sub_agent itself was cancelled but the BE didn't emit per-tcid
 * `tool_status: interrupted` events for the inner tools (it only does so for
 * top-level pending tcids). Without this, inner tool labels render
 * present-tense ("Creating document") even though the parent shows
 * "Sub-agent cancelled". */
function propagateInterruptedToInnerSteps(
  grouped: GroupableMessage[],
  parentCompletedAt: number,
): void {
  for (const m of grouped) {
    if (m.messageType !== TOOL_GROUP_MESSAGE_TYPE) continue
    const artifacts = m.turnArtifacts as ToolGroupArtifacts | undefined
    if (!artifacts) continue
    let mutated = false
    for (const step of artifacts.steps) {
      if (step.status !== "in_progress") continue
      step.status = "interrupted"
      step.completedAt = step.completedAt ?? parentCompletedAt
      mutated = true
      // Recurse into nested sub_agents — a cancelled outer also implies
      // every still-running descendant is cancelled at the same instant.
      if (step.innerMessages && step.innerMessages.length > 0) {
        propagateInterruptedToInnerMessages(step.innerMessages, parentCompletedAt)
      }
    }
    if (mutated) {
      artifacts.isStreaming = artifacts.steps.some((s) => s.status === "in_progress")
    }
  }
}

/** Same propagation but for the post-`toInnerMessages` shape used by
 * `step.innerMessages`. Mini-chats render directly from this array. */
function propagateInterruptedToInnerMessages(
  inner: InnerMessage[],
  parentCompletedAt: number,
): void {
  for (const im of inner) {
    if (im.messageType !== TOOL_GROUP_MESSAGE_TYPE) continue
    const steps = (im as InnerMessage & { steps?: ToolStep[] }).steps
    if (!steps) continue
    for (const step of steps) {
      if (step.status !== "in_progress") continue
      step.status = "interrupted"
      step.completedAt = step.completedAt ?? parentCompletedAt
      if (step.innerMessages && step.innerMessages.length > 0) {
        propagateInterruptedToInnerMessages(step.innerMessages, parentCompletedAt)
      }
    }
  }
}

/** Narrow a `groupImpl` result down to the subset the mini-chat renderer
 * needs, and drop user messages (sub-agents don't have user turns). */
function toInnerMessages(grouped: GroupableMessage[]): InnerMessage[] {
  return grouped
    .filter((m) => m.turnType === "assistant")
    .map((m) => ({
      id: m.id,
      messageType: m.messageType,
      content: m.content,
      turnArtifacts: m.turnArtifacts,
      timestamp: m.timestamp,
      completedAt: m.completedAt,
    }))
}

function mergeContent(a: string | undefined, b: string | undefined): string | undefined {
  if (!a) return b
  if (!b) return a
  return `${a}\n\n${b}`
}

function parseTimestampMs(ts: string | undefined): number | undefined {
  if (!ts) return undefined
  const parsed = Date.parse(ts)
  return Number.isNaN(parsed) ? undefined : parsed
}
